import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTips } from '@/hooks/useTips';
import { useFundamentals } from '@/hooks/useFundamentals';
import type { TipItem } from '@/api/types';

interface HomeTipsProps {
  currentScore: number;
}

function matchesTip(tip: TipItem, scorePercent: number): boolean {
  if (!tip.active) return false;
  if (tip.operator === '>=') return scorePercent >= tip.threshold;
  if (tip.operator === '<') return scorePercent < tip.threshold;
  if (tip.operator === 'between') {
    return scorePercent >= tip.threshold && scorePercent <= (tip.thresholdMax ?? tip.threshold);
  }
  return false;
}

export function HomeTips({ currentScore }: HomeTipsProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split('-')[0];
  const scorePercent = Math.round((currentScore / 5) * 100);

  const { data: allTips = [] } = useTips();
  const { data: fundamentals = [] } = useFundamentals();

  const pickedIndexRef = useRef<number | null>(null);

  const tip = useMemo(() => {
    pickedIndexRef.current = null; // reset when tips/score change
    const matched = allTips.filter(tip => matchesTip(tip, scorePercent));
    if (matched.length === 0) return null;
    if (pickedIndexRef.current === null) {
      pickedIndexRef.current = Math.floor(Math.random() * matched.length);
    }
    return matched[pickedIndexRef.current % matched.length] ?? matched[0];
  }, [allTips, scorePercent]);

  if (!tip) return null;

  const color = fundamentals.find(f => f.id === tip.fundamentalId)?.color ?? 'var(--cp-purple)';
  const decoIcon = tip.tipType === 'crecer' ? 'fa-seedling' : 'fa-lightbulb';

  return (
    <div className="home-card home-tip-card" style={{ borderLeft: `5px solid ${color}` }}>
      <div className="home-tip-body">
        <div className="home-tip-label" style={{ color }}>
          <i className="fas fa-lightbulb" aria-hidden="true" />
          {t('survey.tips.tipOfDay')}
        </div>
        <p className="home-tip-text">{tip.translations?.[lang] ?? tip.tipText}</p>
      </div>
      <div className="home-tip-deco" style={{ color }} aria-hidden="true">
        <i className={`fas ${decoIcon}`} />
      </div>
    </div>
  );
}
